/** What the student is told about their own answer.
 *
 * The server states it (`my_response` on the student's state frame); this module only renders
 * that statement. Nothing here reads `option.correct`: the server strips the answer key from a
 * student's item, so deriving a verdict on the device could only ever produce "wrong".
 * An absent or unknown verdict is neutral, never wrong.
 */
import type { OptionState } from "../ui";

export type Answer = string | string[] | number | null;

export type MyResponse =
  | { item_id: string; attempt: number; answer: unknown; confidence: number | null; channel: string; correct: boolean | null }
  | null
  | undefined;

export type Verdict = "correct" | "incorrect" | "unknown";

/** Answers compare by content: two multi-select arrays are the same only in the same order. */
export function sameAnswer(a: Answer | unknown, b: Answer | unknown): boolean {
  if (Array.isArray(a) || Array.isArray(b)) {
    if (!Array.isArray(a) || !Array.isArray(b) || a.length !== b.length) return false;
    return a.every((x, i) => x === b[i]);
  }
  return a === b;
}

/** The server's record, but only when it is about the item and attempt now on screen: during
 * a re-vote the previous attempt's answer and verdict must not repaint the new round. */
export function mine(my: MyResponse, itemId: string | undefined, attempt: number | undefined): MyResponse {
  if (!my || my.item_id !== itemId || my.attempt !== attempt) return null;
  return my;
}

/** While the item accepts answers the student's working selection is theirs to see; once it
 * stops accepting, the answer the server counted is shown — a refused late tap is not it.
 * With no record at all the untouched selection stays on screen, but `optionState` below is
 * what decides whether it may be labelled as sent. */
export function shownAnswer(open: boolean, local: Answer, my: MyResponse): Answer {
  return open || !my ? local : (my.answer as Answer);
}

/** The seq of the newest submission this device made, and the item it was made for. */
export type Pending = { key: string; seq: number } | null;

/** Does this acknowledgement (or refusal) speak for what is on screen? Only the reply to the
 * newest submission for the item now shown does. An older one is history — a second tap on a
 * slow link, or a queued frame flushed on reconnect — and applying it would snap the selection
 * back to a superseded answer and accuse a change the server did take. */
export function answersLatest(
  d: { item_id?: unknown; attempt?: unknown; seq?: unknown },
  itemId: string,
  attempt: number,
  pending: Pending,
): boolean {
  if (d.item_id !== itemId || d.attempt !== attempt) return false;
  if (!pending || pending.key !== `${itemId}:${attempt}`) return false;
  return d.seq === pending.seq;
}

export function verdictOf(revealed: boolean, my: MyResponse): Verdict {
  if (!revealed || !my || my.correct == null) return "unknown";
  return my.correct ? "correct" : "incorrect";
}

export function optionState(
  key: string,
  s: { open: boolean; revealed: boolean; shown: Answer; sent: Answer; my: MyResponse },
): OptionState {
  const chosen = Array.isArray(s.shown) ? s.shown.includes(key) : s.shown === key;
  // a selection this device never sent and the server holds no record of is a draft, not an
  // answer: once the item stops accepting it is never dressed up as sent, correct or wrong
  if (!s.open && !s.my && s.sent === null) return "disabled";
  if (s.revealed && chosen) {
    const v = verdictOf(true, s.my);
    return v === "unknown" ? "submitted" : v;
  }
  if (!s.open) return chosen ? "submitted" : "disabled";
  if (chosen) return sameAnswer(s.sent, s.shown) ? "submitted" : "selected";
  return "default";
}
